package com.ives.utils.math;

import com.ives.utils.math.LineSegment;
import com.ives.utils.math.GeometryUtils;
import Math;
class MathStats {
	public static function distance2(x1:Float, y1:Float, x2:Float, y2:Float):Float{
		var dx:Float = x1 - x2;
		var dy:Float = y1 - y2;
		
		return(dx*dx + dy*dy);
	}
	
	public static function formatLengthInMm(mm:Float):String{
		return Std.string(roundTo(mm, 1));
	}

	public static function roundTo(value:Float, precision:UInt = 0):Float{
		var scale:Float = Math.pow(10, precision);

		return Math.fround(scale * value) / scale;
	}
	
	public static function formatAreaInMm2(mm2:Float):String{
		var cm2:Float = 100;
		
		if(mm2 < cm2)
			return roundTo(mm2, 2) + " mm²";
		else
			return roundTo(mm2/cm2, 2) + " cm²";
	}

	/* https://en.wikipedia.org/wiki/Shoelace_formula */
	public static function polygonArea(points:Array<Float>):Float{
		var sum:Float = 0;
		var x:Float = 0;
		var y:Float = 0;
		var x_:Float = 0;
		var y_:Float = 0;

		for(n in 0...Std.int(points.length/2)){
			x = points[(n * 2 + 0) % points.length];
			x_ = points[(n * 2 + 2) % points.length];
			y = points[(n * 2 + 1) % points.length];
			y_ = points[(n * 2 + 3) % points.length];

			sum += x * y_ - x_ * y;
		}

		return Math.abs(sum / 2);
	}

	public static function polygonIntersects(points:Array<Float>):Bool{
		var line1:LineSegment = new LineSegment();
		var line2:LineSegment = new LineSegment();

		var segments:UInt = Std.int(points.length/2);

		for(n in 0...segments){
			line1.first.x = points[(n * 2 + 0) % points.length];
			line1.first.y = points[(n * 2 + 1) % points.length];
			line1.second.x = points[(n * 2 + 2) % points.length];
			line1.second.y = points[(n * 2 + 3) % points.length];

			for(m in 0...segments){
				if(m == n || m == (n - 1) || m == (n + 1) || (n == 0 && m == segments - 1) || (n == segments - 1 && m == 0))
					continue;

				line2.first.x = points[(m * 2 + 0) % points.length];
				line2.first.y = points[(m * 2 + 1) % points.length];
				line2.second.x = points[(m * 2 + 2) % points.length];
				line2.second.y = points[(m * 2 + 3) % points.length];

				if(GeometryUtils.doLinesIntersect(line1, line2))
					return true;
			}
		}

		return false;
	}

	public static function median(values:Array<Float>):Float{
		if(values.length == 0)
			return 0;
		
		if(values.length == 1)
			return values[0];
		
		values.sort(sortByNumber);
		
		if(values.length == 2){
			return((values[0] + values[1]) / 2);
		}
		if(values.length % 2 == 0){
			return((values[Std.int(values.length / 2 - 1)] + values[Std.int(values.length / 2 + 1)]) / 2);
		}
		else
			return(values[Std.int((values.length - 1) / 2)]);
	}
	
	public static function average(values:Array<Float>):Float{
		if(values.length == 0)
			return 0;
		
		var sum:Float = 0;
		
		for(value in values)
			sum += value;
		
		return(sum / values.length);
	}

	public static function averagePoints(values:Array<Vector2D>):Vector2D{
		var sum:Vector2D = new Vector2D(0, 0);

		if(values.length == 0)
			return sum;

		for(value in values)
			sum.increment(value);

		sum.scale(1/values.length);

		return sum;
	}

	public static function sortByNumber(a:Float, b:Float):Int{
		return Std.int(a - b);
	}
	
	public static function angleBetweenVectors(dx1:Float, dy1:Float, dx2:Float, dy2:Float):Float{
		var len1:Float = Math.sqrt(dx1 * dx1 + dy1 * dy1);
		var len2:Float = Math.sqrt(dx2 * dx2 + dy2 * dy2);
		
		dx1 /= len1;
		dy1 /= len1;
		dx2 /= len2;
		dy2 /= len2;
		
		return (180 / Math.PI) * Math.abs(Math.acos(dx1 * dx2 + dy1 * dy2));
	}

	public static function pointInPoly(x:Float, y:Float, points:Array<Vector2D>):Bool {
		var sides:UInt = points.length; //amount of sides the polygon has

		var i:UInt = 0;
		var j:UInt = sides - 1;
		var oddNodes:Bool = false; //how many sides have we passed through?
		
		while(i < sides){ //for each side
			if((points[i].y < y && points[j].y >= y) || (points[j].y < y && points[i].y >= y)){ //detect edges
				if(points[i].x+(y-points[i].y)/(points[j].y-points[i].y)*(points[j].x-points[i].x) < x){ //detect edges
					oddNodes = !oddNodes; //if that was an edge, switch oddNodes
				}
			}

			j = i;
			i++;
		}
		
		return oddNodes; // return oddNodes
	}

	public static function pointInEllipsis(x:Float, y:Float, ex:Float, ey:Float, rx:Float, ry:Float):Bool {
		var dx:Float = x - ex;
		var dy:Float = y - ey;

		var inside:Bool = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) <= 1;

		return inside;
	}

	public static function max(values:Array<Float>, ?d:UInt = 0):Float{
		var max:Float = values[d];
		var n:UInt = d;

		while(n < values.length){
			if(values[n] > max)
				max = values[n];

			n += 2;
		}

		return max;
	}

	public static function min(values:Array<Float>, ?d:UInt = 0):Float{
		var min:Float = values[d];
		var n:UInt = d;

		while(n < values.length){
			if(values[n] < min)
				min = values[n];

			n += 2;
		}

		return min;
	}


	public static function lineLength(startX:Float, startY:Float, endX:Float, endY:Float):Float{
		var dx:Float = endX - startX;
		var dy:Float = endY - startY;

		return Math.sqrt(dx * dx + dy * dy);
	}

	public static function area(startX:Float, startY:Float, endX:Float, endY:Float):Float{
		var dx:Float = endX - startX;
		var dy:Float = endY - startY;

		return Math.abs(dx * dy);
	}

	public static function ellipsisArea(startX:Float, startY:Float, endX:Float, endY:Float):Float{
		var a:Float = (endX - startX) / 2;
		var b:Float = (endY - startY) / 2;

		return Math.abs(Math.PI * a * b);
	}

	public static function shapeArea(points:Array<Float>):Float{
		return MathStats.polygonArea(points);
	}

	public static function angle(x1:Float, y1:Float, x2:Float, y2:Float, x3:Float, y3:Float):Float{
		var radians = Math.atan2(y1 - y2, x1 - x2) - Math.atan2(y3 - y2, x3 - x2);
		var angle = 180 * Math.abs(radians) / Math.PI;

		angle = angle % 360;
		angle = angle > 180 ? 360 - angle : angle;

		return angle;
	}

	public static function cobbAngle(x1:Float, y1:Float, x2:Float, y2:Float, x3:Float, y3:Float, x4:Float, y4:Float):Float{
		var radians = Math.atan2(y1 - y2, x1 - x2) - Math.atan2(y4 - y3, x4 - x3);
		var angle = 180 * Math.abs(radians) / Math.PI;

		angle = angle % 360;
		angle = angle > 180 ? 360 - angle : angle;
		angle = angle > 90 ? 180 - angle : angle;

		return angle;
	}

	public static function toRadians(degrees:Float):Float{
		return Math.PI * degrees / 180;
	}

	public static function toDegrees(radians:Float):Float{
		return 180 * radians / Math.PI;
	}
}
